<!--
  OverrideHistory.vue
  콜봇 월 정산 · 요율 탭(화면 명세 2-1)의 회사 예외 이력(늘 펼쳐 모든 버전을 보인다)과 버전 삭제.

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-1", "요청 계약 3-2·3-3"
  - 적용 시작 월 내림차순. 버전마다 쓰는 기간(다음 버전 전 달까지 — 예외 버전끼리는 쌓이지 않는다),
    덮은 칸과 값, 메모, 수정 시각·사람, 이 버전을 쓴 확정 정산 수. 이 달에 쓰는 버전에 "이 달 적용".
  - 요금표 v70 이전에 저장한 버전에 남은 예전 칸(`ai.price_per_unit`·`counselor.*.price` — ~/md/front/admin-콜봇-월정산-
    call-api-구현회신.md "2026-10-16 개정" 2)은 GET 에 저장된 그대로 온다. 계산에 쓰지 않아 덮은 칸 수에 세지 않고,
    "(v70 이전 칸 — 계산에 쓰지 않음)"으로 따로 보인다.
  - `[삭제]`: 확인창 뒤 `DELETE .../plan-overrides/:effectiveFrom`. 확정 정산이 쓴 버전은 비활성 + 툴팁.
    409 PLAN_IN_USE(그새 확정 정산이 씀)·404 PLAN_OVERRIDE_NOT_FOUND(그새 지워짐)는 알리고 다시 받게 한다.
  - 목록을 부르는 일과 쓰기 뒤 안내는 요율 탭(RatesTab.vue)이 한다. 이 컴포넌트는 그리기와 삭제만 한다.

  - props: items(예외 버전들) · costRates(단위 표기) · appliedVersion(이 달에 쓰는 버전의 effective_from) ·
    loading(처음 받는 중) · failed(목록을 받지 못함 — 부모가 오류를 보인다) · api · companyId
  - emits: deleted({ effectiveFrom, alreadyGone }) — 지웠거나 이미 없음: 예외 목록과 상세를 다시 받는다 ·
    refresh() — 목록이 낡음: 예외 목록만 다시 받는다
-->
<template>
  <div class="override-history">
    <a-skeleton v-if="loading && !rows.length" active :title="false" :paragraph="{ rows: 2 }" />

    <template v-else-if="rows.length">
      <h4 :id="listId" class="override-history__title">예외 이력 {{ rows.length }}건</h4>

      <!-- Every version, always open -->
      <ol class="override-history__list" :aria-labelledby="listId">
        <li v-for="row in rows" :key="row.key" class="override-version" :class="{ 'is-applied': row.applied }">
          <div class="override-version__head">
            <span class="override-version__name">{{ row.effectiveFrom }} 버전</span>
            <span class="override-version__range">{{ row.rangeText }}</span>
            <span v-if="row.applied" class="override-version__pill is-applied">이 달 적용</span>
            <span class="override-version__pill">{{ row.cellsLabel }}</span>
            <span class="override-version__pill" :class="{ 'is-used': row.used > 0 }">
              확정 정산 {{ formatNumber(row.used) }}건
            </span>
            <span class="override-version__actions">
              <a-tooltip :title="row.blockReason || undefined">
                <a-button
                  size="small"
                  danger
                  :disabled="!!row.blockReason || (busyKey !== null && busyKey !== row.key)"
                  :loading="busyKey === row.key"
                  @click="confirmDelete(row)"
                >
                  삭제
                </a-button>
              </a-tooltip>
            </span>
          </div>

          <ul v-if="row.cells.length" class="override-version__cells" :aria-label="`${row.effectiveFrom} 버전이 덮은 칸`">
            <li v-for="cell in row.cells" :key="cell.key" class="override-version__cell">
              {{ cell.label }} <b>{{ cell.text }}</b>
            </li>
          </ul>
          <p v-else class="override-version__none">덮은 칸 없음 — 이 버전부터 예외 없이 기본 요율만 씁니다.</p>

          <ul
            v-if="row.legacyCells.length"
            class="override-version__cells is-legacy"
            :aria-label="`${row.effectiveFrom} 버전에 남은 v70 이전 칸`"
          >
            <li v-for="cell in row.legacyCells" :key="cell.path" class="override-version__cell">
              {{ cell.label }} <b>{{ cell.text }}</b>
            </li>
            <li class="override-version__legacy-note">v70 이전 칸 — 계산에 쓰지 않음, 이 버전을 다시 저장하면 빠집니다</li>
          </ul>

          <p v-if="row.memo" class="override-version__memo">메모 · {{ row.memo }}</p>
          <p class="override-version__meta">{{ row.updatedText }}</p>
        </li>
      </ol>
    </template>

    <!-- 목록을 받지 못했으면 "없음"이라고 말하지 않는다(부모가 오류와 다시 시도를 보인다) -->
    <p v-else-if="!failed" class="override-history__empty">예외 이력 없음</p>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { Modal, message } from 'ant-design-vue';

import {
  addMonths,
  formatKstDateTime,
  formatNumber,
  formatUnitPrice,
  isNumber,
  isValidMonth,
} from '../../service/settlementFormat';
import {
  actorLabel,
  legacyOverrideCells,
  overrideCellSummaries,
  overrideDeleteBlockReason,
} from '../../service/settlementRules';
import { getCallApiErrorBody, getCallApiErrorCode, parseCallApiError } from '@/views/Callbot/service/callApiError';

const props = defineProps({
  /** 이 회사 예외 버전들(GET .../plan-overrides `items`) */
  items: { type: Array, default: () => [] },
  /** `cost_rates` — 덮은 값의 단위 표기(10초·3분 등)에 쓴다 */
  costRates: { type: Object, default: null },
  /** 이 달에 쓰는 예외 버전(`rates.override_version`) — "이 달 적용" */
  appliedVersion: { type: String, default: null },
  /** 목록을 처음 받는 중 */
  loading: { type: Boolean, default: false },
  /** 목록을 받지 못했다 */
  failed: { type: Boolean, default: false },
  /** settlementApi 또는 목(getSettlementApi()) */
  api: { type: Object, required: true },
  companyId: { type: String, required: true },
});

const emit = defineEmits(['deleted', 'refresh']);

const listId = `st-override-history-${getCurrentInstance()?.uid ?? 0}`;
/** 지우는 중인 버전(effective_from) */
const busyKey = ref(null);

const sortedItems = computed(() =>
  (Array.isArray(props.items) ? props.items : [])
    .filter((item) => isValidMonth(item?.effective_from))
    .slice()
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))
);

/** 수정자 — 고친 사람이 있으면 그 사람, 없으면 만든 사람(이름이 없으면 id) */
function editorOf(item) {
  if (item.updated_by) return actorLabel(item.updated_by, item.updated_by_name);
  return actorLabel(item.created_by, item.created_by_name);
}

const rows = computed(() =>
  sortedItems.value.map((item, index) => {
    // 내림차순이라 바로 앞 행이 다음(더 늦은) 버전이다 — 이 버전은 그 전 달까지만 쓴다
    const newer = index > 0 ? sortedItems.value[index - 1] : null;
    const until = newer ? addMonths(newer.effective_from, -1) : null;
    const used = isNumber(item.confirmed_statements) ? item.confirmed_statements : 0;
    const cells = overrideCellSummaries(item, props.costRates);
    // 예전 칸의 값은 판매가(원) 그대로 — 단위는 칸마다 달라 붙이지 않는다
    const legacyCells = legacyOverrideCells(item).map((c) => ({
      path: c.path,
      label: c.label,
      text: isNumber(c.value) ? `${formatUnitPrice(c.value)}원` : '미정',
    }));
    const at = isNumber(item.updated_at) ? item.updated_at : item.created_at;
    let rangeText = `${item.effective_from}부터 계속 씀`;
    if (until === item.effective_from) rangeText = `${item.effective_from} 한 달만 씀`;
    else if (until) rangeText = `${item.effective_from} ~ ${until}에 씀`;
    return {
      key: item.effective_from,
      effectiveFrom: item.effective_from,
      rangeText,
      applied: !!props.appliedVersion && item.effective_from === props.appliedVersion,
      cells,
      legacyCells,
      cellsLabel: cells.length ? `덮은 칸 ${cells.length}개` : '예외 없음',
      used,
      memo: typeof item.memo === 'string' ? item.memo.trim() : '',
      updatedText: `수정 ${formatKstDateTime(at)} · ${editorOf(item)}`,
      // 확정 정산이 쓴 버전은 삭제 버튼을 끄고 툴팁으로 이유를 보인다
      blockReason: overrideDeleteBlockReason(item) ?? '',
    };
  })
);

function confirmDelete(row) {
  if (row.blockReason || busyKey.value !== null) return;
  Modal.confirm({
    title: '예외 버전 삭제',
    content: `${row.effectiveFrom} 예외 버전을 삭제합니다. 이 버전을 쓰던 달은 직전 예외 버전(없으면 기본 요율)을 따릅니다. 추정 중인 달은 금액이 바로 바뀌고, 검토 필요인 달은 정산 탭의 [다시 계산]을 눌러야 정산 스냅샷에 들어갑니다.`,
    okText: '삭제',
    okType: 'danger',
    cancelText: '취소',
    // 실패해도 resolve 한다 — 확인창은 닫고 알림으로 알린다
    onOk: () => remove(row),
  });
}

async function remove(row) {
  busyKey.value = row.key;
  try {
    await props.api.deletePlanOverride(props.companyId, row.effectiveFrom);
    emit('deleted', { effectiveFrom: row.effectiveFrom, alreadyGone: false });
  } catch (err) {
    const code = getCallApiErrorCode(err);
    if (code === 'PLAN_OVERRIDE_NOT_FOUND') {
      // 그새 다른 사람이 지웠다 — 지운 것과 같으니 목록과 상세를 다시 받는다
      message.warning('이미 삭제된 예외 버전입니다. 목록을 다시 불러옵니다.');
      emit('deleted', { effectiveFrom: row.effectiveFrom, alreadyGone: true });
    } else if (code === 'PLAN_IN_USE') {
      // 그새 이 버전을 쓴 정산이 확정됐다 — 확정 정산 수를 새로 받는다
      const used = getCallApiErrorBody(err)?.error?.details?.confirmed_statements;
      const who = isNumber(used) ? `확정 정산 ${formatNumber(used)}건이` : '확정 정산이';
      message.warning(`${who} 쓴 버전이라 삭제할 수 없습니다. 목록을 다시 불러옵니다.`);
      emit('refresh');
    } else {
      message.error(parseCallApiError(err, '예외 버전을 삭제하지 못했습니다.'));
    }
  } finally {
    busyKey.value = null;
  }
}
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.override-history {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;

  &__title {
    margin: 0;
    font-size: 12.5px;
    font-weight: 600;
    color: $st-text;
  }

  &__empty {
    margin: 0;
    color: $st-muted;
    font-size: 12.5px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
}

.override-version {
  padding: 10px 12px;
  border: 1px solid $st-border;
  border-radius: 10px;
  background: #fff;

  // 이 달에 쓰는 버전 — 요율 출처 `예외`와 같은 노랑
  &.is-applied {
    border-color: $st-override-border;
    background: $st-override-bg;
  }

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
  }

  &__name {
    font-weight: 700;
    color: $st-text;
    @include st-num;
  }

  &__range {
    color: $st-muted;
    font-size: 12px;
    @include st-num;
  }

  &__pill {
    @include st-pill;

    &.is-applied {
      @include st-pill-tone($st-override, #fff, $st-override-border);
    }

    &.is-used {
      @include st-pill-tone($st-accent, $st-accent-bg, $st-accent-border);
    }
  }

  &__actions {
    margin-left: auto;
  }

  &__cells {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 6px;
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
  }

  &__cell {
    padding: 1px 8px;
    border: 1px solid $st-border;
    border-radius: 6px;
    background: $st-bg;
    color: $st-text-secondary;
    font-size: 12px;

    b {
      color: $st-text;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
  }

  &__none {
    margin: 8px 0 0;
    color: $st-muted;
    font-size: 12px;
  }

  // v70 이전 칸 — 흐리게, 취소선 없이(값은 저장된 그대로다)
  &__cells.is-legacy &__cell {
    border-style: dashed;
    background: #fff;
    color: $st-muted;

    b {
      color: $st-muted;
    }
  }

  &__legacy-note {
    align-self: center;
    color: $st-muted;
    font-size: 11.5px;
  }

  &__memo {
    margin: 6px 0 0;
    color: $st-text-secondary;
    font-size: 12.5px;
    line-height: 1.5;
    word-break: break-word;
  }

  &__meta {
    margin: 4px 0 0;
    color: $st-muted;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  }
}
</style>
